/**
 * List of complex plurals to be used when converting singular nouns to their
 * plural forms.
 */
const knownPlurals: Record<string, string> = {
  leaf: 'leaves',
  it: 'them',
};

function endsWithConsonantAndY(str: string) {
  const consonantYRegex = /[bcdfghjklmnpqrstvwxz]y$/i;
  return !!str.match(consonantYRegex);
}

/**
 * This function converts singular nouns to their plural forms. It handles basic
 * cases by adding an "s" or changing "y" to "ies". To handle complex plural
 * forms, add them to the `knownPlurals` map.
 *
 * Count can be passed to return singular form if count is 1.
 *
 * @example
 * get("order") // "orders"
 * get("company") // "companies"
 */
export function getPlural(singular: string, count?: number): string {
  if (count === 1) {
    return singular;
  }

  const knownPlural = knownPlurals[singular.toLowerCase()];
  if (knownPlural) {
    return knownPlural;
  } else if (endsWithConsonantAndY(singular)) {
    return singular.replace(/y$/i, 'ies');
  }
  return `${singular}s`;
}

export function pluralize(singular: string, count: number) {
  let text = singular;
  if (count !== 1) {
    text = getPlural(singular, count);
  }
  return `${count} ${text}`;
}

/**
 * Type Guard to check if value is string.
 */
export const isString = (value: unknown): value is string => {
  return typeof value === 'string';
};

/**
 * Truncate a string to a given length, adding an ellipsis if it was truncated
 * @param str - The string to truncateWords
 * @param num - The maximum length of the string including ellipsis
 * @returns The truncated string
 * @example
 * ```js
 * truncateWords('Hello world', 5) // 'Hello...'
 * truncateWords('Hello world', 10) // 'Hello...'
 * truncateWords('Hello world', 11) // 'Hello...'
 * truncateWords('Hello world', 12) // 'Hello world'
 * ```
 */
export const truncateWords = (str: string, num = 155): string => {
  const ellipsis = '...';

  if (str.length <= num) {
    return str;
  }

  const words = str.split(' ');
  let truncatedStr = '';

  for (const word of words) {
    if (!!truncatedStr.length && truncatedStr.length + word.length + 1 + ellipsis.length > num) {
      return truncatedStr.trim() + ellipsis;
    }
    truncatedStr += word + ' ';
  }

  return truncatedStr.trim();
};

/**
 * Capitalize the first letter of a string
 *
 * @example
 * ```ts
 * capitalizeFirstLetter('hello') // 'Hello'
 * ```
 */
export const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

/**
 * Convert a snake_case to Title Case
 *
 * @example
 * ```ts
 * snakeToTitleCase('hello_world') // 'Hello World'
 * ```
 */
export const snakeToTitleCase = (str: string) => {
  return str
    .split('_')
    .map((word) => capitalizeFirstLetter(word))
    .join(' ');
};

/**
 * Convert string to a URL friendly slug.
 *
 * @param str - The string to slugify
 * @returns The slugified string
 *
 * @example
 * ```ts
 * slugify(" Althea's THC_CBD") // 'altheas-thc-cbd'
 * ```
 */
export const slugify = (str: string) => {
  return str
    .replace(/'/g, '') // remove apostrophes
    .replace(/[^a-zA-Z0-9]/g, '-') // replace non-alphanumeric characters with hyphens
    .replace(/-+/g, '-') // replace multiple hyphens with a single hyphen
    .replace(/^-|-$/g, '') // remove leading and trailing hyphens
    .toLowerCase();
};

/**
 * Convert words in a string to uppercase.
 *
 * @param words - The words found in a string
 * @param str - The string to transform
 * @returns The transformed string
 *
 * @example
 * ```ts
 * wordsToUppercase(['cbd', 'thc'], 'cbd thc hybrid') // 'CBD THC hybrid'
 * ```
 */
export const wordsToUppercase = (words: string[], str: string) => {
  const wordRegex = new RegExp(`\\b(${words.join('|')})\\b`, 'gi');

  return str.replace(wordRegex, (match) => match.toUpperCase());
};

/**
 * Formats a string to be suitable for use as a testId.
 * This function removes all non-alphanumeric characters and converts the string to lowercase.
 * This is useful for ensuring that testIds are consistent and do not contain spaces or special characters
 * @param str - The string to format for testId usage
 * @returns The formatted string suitable for use as a testId
 */
export const formatStringForTestId = (str: string): string => {
  const formatted = str.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

  // if the string starts with a number, prepend 'id' to ensure it is a valid testId
  if (/^\d/.test(formatted)) {
    return `id${formatted}`;
  }
  return formatted;
};

/**
 * Convert a slug into Title Case.
 *
 * Examples:
 *   slugToTitleCase('craft-botanics_420') // 'Craft Botanics 420'
 */
export const slugToTitleCase = (slug: string): string => {
  const cleaned = slug
    .replace(/(^[-_]+|[-_]+$)/g, '') // trim leading/trailing separators
    .replace(/[-_]+/g, ' ') // convert separators to spaces
    .replace(/[^a-zA-Z0-9\s']/g, ' ') // remove any unexpected chars, keep apostrophes
    .replace(/\s+/g, ' ') // remove multiple spaces
    .trim();

  const titled = cleaned
    .split(' ')
    .map((word) => capitalizeFirstLetter(word.toLowerCase()))
    .join(' ');

  return titled;
};
